import API from '@aws-amplify/api';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShipyardContext } from '../Context';

import LanguageSelector from '../shared/LanguageSelector';
import View from '../shared/View';
import Grid from '../shared/Grid';
import DateTimeText from '../shared/DateTimeText';
import Tile from '../shared/Tile';

import './Dashboard.scss';
import { Tab, TabContainer } from '../shared/TabControl';
import MenuButton from '../shared/MenuButton';

function Dashboard() {
    const [t] = useTranslation();
    const { userSettings } = useContext(ShipyardContext);
    const [doingAjax, setDoingAjax] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [gridData, setGridData] = useState();
    const [gridHeaders, setGridHeaders] = useState();
    const [actionState, setActionState] = useState(null);

    useEffect(() => {
        (async () => {
            let blueCircle = <span className="blue circle" />;
            let purpleCircle = <span className="purple circle" />;
            let pinkCircle = <span className="pink circle" />;
            if (dashboardData === null) {
                let response = await API.get('ShipyardAPI', '/dashboard');
                setDashboardData(response);
                setGridHeaders([
                    {
                        colLabel: 'STN',
                        colName: 'studentNumber',
                        sortFunc: (dir) => {
                            if (!dir) {
                                return (a, b) => {
                                    return a['studentNumber'] - b['studentNumber'];
                                };
                            } else {
                                return (a, b) => {
                                    return b['studentNumber'] - a['studentNumber'];
                                };
                            }
                        },
                        filterOn: false,
                    },
                    {
                        colLabel: 'Student',
                        colName: 'name',
                        sortFunc: (dir) => {
                            if (!dir) {
                                return (a, b) => {
                                    if (a['name'] < b['name']) {
                                        return 1;
                                    } else {
                                        return -1;
                                    }
                                };
                            } else {
                                return (a, b) => {
                                    if (b['name'] < a['name']) {
                                        return 1;
                                    } else {
                                        return -1;
                                    }
                                };
                            }
                        },
                        filterOn: false,
                    },
                    {
                        colLabel: 'DOB',
                        colName: 'birthday',
                        sortFunc: (dir) => {
                            if (!dir) {
                                return (a, b) => {
                                    return a['birthday'] - b['birthday'];
                                };
                            } else {
                                return (a, b) => {
                                    return b['birthday'] - a['birthday'];
                                };
                            }
                        },
                        filterOn: false,
                    },
                    {
                        colLabel: 'IEP/ISP',
                        colName: 'code',
                        sortFunc: (dir) => {
                            if (!dir) {
                                return (a, b) => {
                                    if (a['code'] < b['code']) {
                                        return 1;
                                    } else {
                                        return -1;
                                    }
                                };
                            } else {
                                return (a, b) => {
                                    if (b['code'] < a['code']) {
                                        return 1;
                                    } else {
                                        return -1;
                                    }
                                };
                            }
                        },
                        filterOn: true,
                    },
                    {
                        colLabel: 'Due',
                        colName: 'due',
                    },
                    {
                        colLabel: 'Type',
                        colName: 'type',
                        sortFunc: (dir) => {
                            if (!dir) {
                                return (a, b) => {
                                    if (a['type'] < b['type']) {
                                        return 1;
                                    } else {
                                        return -1;
                                    }
                                };
                            } else {
                                return (a, b) => {
                                    if (b['type'] < a['type']) {
                                        return 1;
                                    } else {
                                        return -1;
                                    }
                                };
                            }
                        },
                        filterOn: true,
                    },
                    {
                        colLabel: 'Status',
                        colName: 'status',
                        sortFunc: (dir) => {
                            if (!dir) {
                                return (a, b) => {
                                    if (a['status'] < b['status']) {
                                        return 1;
                                    } else {
                                        return -1;
                                    }
                                };
                            } else {
                                return (a, b) => {
                                    if (b['status'] < a['status']) {
                                        return 1;
                                    } else {
                                        return -1;
                                    }
                                };
                            }
                        },
                        filterOn: true,
                    },
                ]);
                setGridData({
                    rows: [
                        {
                            studentNumber: 10001,
                            name: 'Jonah Anderson',
                            birthday: new Date('10/17/2002'),
                            code: 'IEP',
                            type: 'Initial',
                            status: 'SDE Review',
                            //metadata goes here
                            columns: [
                                <span colname="studentNumber">10001</span>,
                                <button
                                    className="button-link"
                                    colname="name"
                                >
                                    Jonah Anderson
                                </button>,
                                <span colname="birthday">10/17/2002</span>,
                                <span colname="code">IEP</span>,
                                <span colname="due">7/12/2021</span>,
                                <span colname="type">Initial</span>,
                                <span colname="status">{blueCircle}&nbsp;SDE Review</span>,
                            ],
                        },
                        {
                            studentNumber: 10002,
                            name: 'Mariah Anderson',
                            birthday: new Date('08/14/2008'),
                            code: 'IEP',
                            type: 'Initial',
                            status: 'School Review',
                            //metadata goes here
                            columns: [
                                <span colname="studentNumber">10002</span>,
                                <button
                                    className="button-link"
                                    colname="name"
                                >
                                    Mariah Anderson
                                </button>,
                                <span colname="birthday">08/14/2008</span>,
                                <span colname="code">IEP</span>,
                                <span colname="due">7/12/2021</span>,
                                <span colname="type">Initial</span>,
                                <span colname="status">{purpleCircle}&nbsp;School Review</span>,
                            ],
                        },
                        {
                            studentNumber: 10003,
                            name: 'Natali Sipes',
                            birthday: new Date('10/17/2002'),
                            code: 'ISP',
                            type: 'Initial',
                            status: 'SDE Review',
                            //metadata goes here
                            columns: [
                                <span colname="studentNumber">10003</span>,
                                <button
                                    className="button-link"
                                    colname="name"
                                >
                                    Natali Sipes
                                </button>,
                                <span colname="birthday">10/17/2002</span>,
                                <span colname="code">ISP</span>,
                                <span colname="due">7/08/2021</span>,
                                <span colname="type">Initial</span>,
                                <span colname="status">{blueCircle}&nbsp;SDE Review</span>,
                            ],
                        },
                        {
                            studentNumber: 10004,
                            name: 'Peter Pettigrew',
                            birthday: new Date('08/14/2008'),
                            code: 'IEP',
                            type: 'Renewal',
                            status: 'DHS Review',
                            //metadata goes here
                            columns: [
                                <span colname="studentNumber">10004</span>,
                                <button
                                    className="button-link"
                                    colname="name"
                                >
                                    Peter Pettigrew
                                </button>,
                                <span colname="birthday">08/14/2008</span>,
                                <span colname="code">IEP</span>,
                                <span colname="due">7/12/2021</span>,
                                <span colname="type">Renewal</span>,
                                <span colname="status">{pinkCircle}&nbsp;DHS Review</span>,
                            ],
                        },
                    ],
                });
            }
        })();
    }, [dashboardData]);

    useEffect(() => {
        if (doingAjax && dashboardData !== null && userSettings.Retrieved) setDoingAjax(false);
    }, [userSettings.Retrieved, dashboardData, doingAjax]);

    useEffect(() => {
        if (!!actionState) console.log(actionState);
    }, [actionState]);

    let dashboardTitle = <h2>{t('Dashboard.Title')}</h2>;

    return (
        <View
            className="dashboard-component"
            doingAjax={doingAjax}
            viewTitle={dashboardTitle}
            name="dashboard"
        >
            <div>
                {t('Dashboard.Placeholder')}
                <div className="dashboard-tile-row">
                    <Tile
                        count={1100}
                        color="blue"
                        title="Admin Tasks"
                    />
                    <Tile
                        count={28}
                        color="pink"
                        title="DHS/PS Tasks"
                    />
                    <Tile
                        count={5}
                        color="orange"
                        title="Parent Tasks"
                    />
                    <Tile
                        count={102}
                        color="green"
                        title="Completed"
                    />
                </div>
                <TabContainer>
                    <Tab title="Grid">
                        {gridData && gridHeaders ? (
                            <Grid
                                title="Cool Grid Title"
                                headers={gridHeaders}
                                data={gridData}
                                pageSize={3}
                                initialSort="birthday"
                                initialSortDirection={false}
                                filters={true}
                                search={true}
                            />
                        ) : null}
                    </Tab>
                    <Tab title="Static Time Text">
                        <DateTimeText type="short" />
                        <DateTimeText type="short-time" />
                        <DateTimeText type="long" />
                        <DateTimeText type="long-time" />
                    </Tab>
                    <Tab title="Relative Time Text">
                        <DateTimeText
                            type="relative"
                            date="2022-01-20T18:00:05.150Z"
                        />
                        <DateTimeText
                            type="relative"
                            date="2022-01-20T08:18:05.150Z"
                        />
                        <DateTimeText
                            type="relative"
                            date="2022-01-18T18:18:05.150Z"
                        />
                    </Tab>
                </TabContainer>
                <MenuButton
                    classes={['OneClass', 'TwoClass', 'RedClass', 'BlueClass']}
                    actions={[
                        {
                            labelKey: 'Withdraw Student',
                            action: () => {
                                setActionState('/withdraw');
                            },
                        },
                        {
                            labelKey: 'Begin New Application',
                            action: () => {
                                setActionState('/begin');
                            },
                        },
                    ]}
                >
                    Actions
                </MenuButton>
                <LanguageSelector />
            </div>
        </View>
    );
}

export default Dashboard;
